import styled from '@emotion/styled';

import React, { useEffect } from 'react';
import * as Yup from 'yup';
import BaseModal from 'ui_system/modals/BaseModal';
import { BaseSelect } from 'ui_system/forms/BaseSelect';
import { Formik, Field, Form } from 'formik';
import RichText from 'ui_system/forms/RichText';

import useWindowDimensions from 'ui_system/utilities/useWindowDimensions';
import {
  AddButton,
  TextField,
  FieldSection,
  Label,
  SubLabel,
  Error as Err,
} from 'ui_system/forms/FormStyles';
import { Button } from 'ui_system/token_auth/elements/Button';
import { Theme } from 'ui_system/theme';

const ModalBody = styled.div`
  height: 100vh;
  overflow: scroll;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    height: 600px;
    overflow: hidden;
  }
`;

const textFieldProps = {
  css: TextField,
  type: 'text',
};

const Actions = styled.div`
  padding: 0px 16px 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    padding: 30px 60px;
    height: 104px;
    border-top: 1px solid ${Theme.colors.inputs.border};
    justify-content: space-between;
  }
`;

const SectionTitle = styled.div`
  color: ${Theme.colors.primaryTextColor};
  text-align: center;
  margin-bottom: 30px;
  text-transform: capitalize;
  font-weight: 496;
  font-size: 15px;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    font-size: 16px;
  }
`;

const SaveButton = styled(Button)`
  width: 140px;
  font-size: 16px;
  padding: 10px 30px;
  background-color: ${(props) =>
    props.brandColor
      ? props.brandColor
      : Theme.colors.linksDesignElementsBaseColor};
  color: white;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    font-size: 16px;
  }
`;

const CancelButton = styled(AddButton)`
  width: 140px;
  background-color: transparent;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  opacity: ${(props) => (props.disabled ? ' 0.3' : '1')};
  color: ${Theme.colors.secondaryTextColor};
  border: 1px solid ${Theme.colors.borderActionableFieldColor};
`;

const InstructionSection = styled.div`
  margin-bottom: 20px;
`;

const ScrollableForm = styled.div`
  padding: 30px 16px 0px;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    height: 500px;
    overflow: scroll;
    padding: 30px 60px;
  }
`;

const Error = styled(Err)`
  margin-bottom: 0px;
`;

const ResourceMeetingForm = ({
  errors,
  dirty,
  values,
  setFieldValue,
  onClose,
  isUpdating,
  isDesktop,
  meetingVendors,
  onlineMeetingId,
  brandColor,
}) => {
  const urlLabel = values.isGlobal ? 'Meeting URL' : 'URL to Connect';
  const urlPlaceholder = values.isGlobal
    ? 'https://zoom.us/123456789'
    : 'https://';
  const onlineMeetingVendorChange = (val) => {
    setFieldValue('meetingVendor', val);
    setFieldValue('isGlobal', val.isGlobal);
    setFieldValue('instructions', val.instructions);
    if (!val.isGlobal) {
      setFieldValue('vendorMeetingId', '');
      setFieldValue('vendorMeetingPassword', '');
    }
  };
  if (onlineMeetingId !== values.id) {
    setFieldValue('id', onlineMeetingId);
  }

  return (
    <Form>
      <ScrollableForm className="onlineModalForm">
        <SectionTitle>Add Video Conferencing Details</SectionTitle>
        <Label fontWeight={500}>Video Conference Service</Label>
        <FieldSection marginBottom={20} data-testid="meeting-vendor-dropdown">
          <Field
            name="meetingVendor"
            render={() => (
              <BaseSelect
                value={values.meetingVendor}
                onChange={onlineMeetingVendorChange}
                options={meetingVendors}
                styles={{ container: () => ({ width: '100%' }) }}
              />
            )}
          />
          {errors.meetingVendor && <Error>{errors.meetingVendor}</Error>}
        </FieldSection>

        <Label fontWeight={500}>{urlLabel}</Label>
        <SubLabel>
          Please enter the URL participants will use to connect to your online
          classes. This will be sent to participants when they book your class.
        </SubLabel>
        <FieldSection marginBottom={20}>
          <Field
            {...textFieldProps}
            name="vendorMeetingUrl"
            placeholder={urlPlaceholder}
          />
          {errors.vendorMeetingUrl && (
            <Error data-testid="vendor-meeting-error">
              {errors.vendorMeetingUrl}
            </Error>
          )}
        </FieldSection>

        {values.isGlobal && (
          <React.Fragment>
            <Label fontWeight={500}>Meeting ID (Optional)</Label>
            <FieldSection marginBottom={20}>
              <Field
                {...textFieldProps}
                name="vendorMeetingId"
                placeholder="123-456-789"
              />
            </FieldSection>

            <Label fontWeight={500}>Meeting Password (Optional)</Label>
            <FieldSection marginBottom={20}>
              <Field
                {...textFieldProps}
                name="vendorMeetingPassword"
                placeholder="123456"
              />
            </FieldSection>
          </React.Fragment>
        )}
        {!values.isGlobal && (
          <React.Fragment>
            <Label fontWeight={500}>Other Instructions (Optional)</Label>
            <SubLabel>
              Please enter any other instructions guests will need in order to
              connect to online experiences (e.g. meeting ID, password,
              additional instructions). This will be included in the email sent
              to guests when they book an online experience.
            </SubLabel>
            <InstructionSection>
              <Field
                name="instructions"
                handleChange={setFieldValue}
                supportImages={false}
                component={RichText}
                minHeight={100}
                bounds=".onlineModalForm"
              />
            </InstructionSection>
          </React.Fragment>
        )}
      </ScrollableForm>
      <Actions data-testid="online-meeting-modal-actions">
        {isDesktop && (
          <CancelButton onClick={onClose} disabled={isUpdating}>
            Cancel
          </CancelButton>
        )}
        <SaveButton
          type="submit"
          disabled={isUpdating}
          faded={!dirty}
          text="Save"
          brandColor={brandColor}
        />
      </Actions>
    </Form>
  );
};

const OnlineMeetingModal = ({
  handleSubmit,
  experienceUpdating,
  brandColor,
  isOpen,
  onClose,
  onlineMeeting,
  onlineMeetingVendors,
}) => {
  const dimensions = useWindowDimensions();
  const isDesktop = dimensions.width >= Theme.mobile.phoneWidth;

  useEffect(() => onClose(), [experienceUpdating]);

  const validationSchema = Yup.object().shape({
    vendorMeetingUrl: Yup.string()
      .nullable()
      .url('Please enter a valid Meeting URL starting with https://')
      .required('Please enter a Meeting URL'),
  });

  const onSubmitForm = (values, actions) => {
    const { instructions, isGlobal } = values;
    const meetingVendor = {
      id: values.meetingVendor.value,
      name: values.meetingVendor.label,
      instructions,
      isGlobal,
    };
    const parsedValues = {
      ...values,
      meetingVendor,
    };
    actions.setSubmitting(false);
    handleSubmit(parsedValues);
  };

  const meetingVendors = onlineMeetingVendors.map((mv) => ({
    value: mv.id,
    label: mv.name,
    instructions: mv.instructions,
    isGlobal: mv.isGlobal,
  }));
  const meetingVendor =
    meetingVendors.find((mv) => mv.value === onlineMeeting.meetingVendorId) ||
    meetingVendors[0];
  if (meetingVendors.every((mv) => mv.isGlobal)) {
    meetingVendors.push({
      value: null,
      label: 'Other',
      isGlobal: false,
      instructions: '',
    });
  }

  const initialValues = {
    isGlobal: meetingVendor.isGlobal,
    instructions: meetingVendor.instructions,
    meetingVendor,
    ...onlineMeeting,
  };

  return (
    <BaseModal close={onClose} open={isOpen} hideClose={isDesktop}>
      <ModalBody>
        <Formik
          render={(formikProps) => (
            <ResourceMeetingForm
              isUpdating={experienceUpdating}
              onClose={onClose}
              isDesktop={isDesktop}
              meetingVendors={meetingVendors}
              brandColor={brandColor}
              onlineMeetingId={onlineMeeting.id}
              {...formikProps}
            />
          )}
          initialValues={initialValues}
          onSubmit={onSubmitForm}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
        />
      </ModalBody>
    </BaseModal>
  );
};

export default OnlineMeetingModal;
